import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "totalAmount", 
    "remainingAmount", 
    "paymentsList", 
    "validateButton",
    "clientSubscriptions",
    "clientCredits",
    "amountInput",
    "remainingAmountButton"
  ]
  
  static values = {
    ticketId: Number,
    ticketTotal: Number,
    subtotals: Object,
    clientSubscriptions: Array,
    clientCredits: Array
  }

  connect() {
    this.provisionalPayments = []
    this.updateDisplay()
    this.initializeAvailableOptions()
  }

  initializeAvailableOptions() {
    // Initialiser les abonnements disponibles
    this.renderClientSubscriptions()
    
    // Initialiser les crédits client avec montants suggérés
    this.renderClientCredits()
  }

  renderClientSubscriptions() {
    if (!this.hasClientSubscriptionsTarget) return
    
    const subscriptions = this.clientSubscriptionsValue || []
    let html = ""
    
    subscriptions.forEach(subscription => {
      
      // Calculer combien de séances de cet abonnement ont déjà été utilisées
      const usedSessions = this.provisionalPayments
        .filter(p => {
          if (p.type !== 'subscription') return false
          
          // Gérer les abonnements du ticket vs les abonnements existants
          if (subscription.from_ticket) {
            return p.data.paymentType === 'subscription_from_ticket' && 
                   p.data.ticketItemId === parseInt(subscription.id.replace('ticket_', ''))
          } else {
            return p.data.paymentType === 'subscription' && 
                   p.data.clientSubscriptionId === subscription.id
          }
        })
        .length
      
      const remainingSessions = subscription.sessions_remaining - usedSessions
      
      // Vérifier si cet abonnement peut être utilisé pour le ticket actuel
      const coverableAmount = this.calculateSubscriptionCoverage(subscription.service_type)
      const canBeUsed = coverableAmount > 0 && remainingSessions > 0
      
      if (remainingSessions > 0) {
        if (canBeUsed) {
          const buttonLabel = subscription.from_ticket ? 
            "Utiliser (acheté sur ce ticket)" : 
            "Utiliser 1 séance"
            
          html += `
            <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="font-medium text-gray-900">${subscription.plan_name}</h4>
                  <p class="text-sm text-gray-600">${remainingSessions} séances restantes</p>
                  <p class="text-xs text-orange-600">Peut couvrir ${this.formatCurrency(coverableAmount)}</p>
                  ${subscription.from_ticket ? '<p class="text-xs text-blue-600">✨ Nouvel abonnement</p>' : ''}
                </div>
                <button type="button" 
                        class="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                        data-action="click->payment-modal#useSubscription"
                        data-subscription-id="${subscription.id}">
                  ${buttonLabel}
                </button>
              </div>
            </div>
          `
        } else {
          html += `
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="font-medium text-gray-500">${subscription.plan_name}</h4>
                  <p class="text-sm text-gray-500">${remainingSessions} séances restantes</p>
                  <p class="text-xs text-gray-500">Aucun service ${this.getServiceTypeLabel(subscription.service_type)} sur ce ticket</p>
                  ${subscription.from_ticket ? '<p class="text-xs text-blue-600">✨ Nouvel abonnement</p>' : ''}
                </div>
                <span class="px-3 py-1 bg-gray-200 text-gray-500 rounded text-sm">
                  Non applicable
                </span>
              </div>
            </div>
          `
        }
      } else {
        // Afficher un message si toutes les séances disponibles ont été utilisées
        html += `
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="font-medium text-gray-500">${subscription.plan_name}</h4>
                <p class="text-sm text-gray-500">Toutes les séances ont été utilisées pour ce paiement</p>
                ${subscription.from_ticket ? '<p class="text-xs text-blue-600">✨ Nouvel abonnement</p>' : ''}
              </div>
              <span class="px-3 py-1 bg-gray-200 text-gray-500 rounded text-sm">
                Épuisé
              </span>
            </div>
          </div>
        `
      }
    })
    
    this.clientSubscriptionsTarget.innerHTML = html
  }

  renderClientCredits() {
    if (!this.hasClientCreditsTarget) return
    
    const credits = this.clientCreditsValue || []
    console.log('renderClientCredits - credits data:', credits)
    let html = ""
    
    credits.forEach(wallet => {
      console.log('Processing wallet:', wallet)
      if (parseFloat(wallet.balance) > 0) {
        const maxUsable = this.calculateMaxUsableAmount(wallet.category)
        const suggestedAmount = Math.min(parseFloat(wallet.balance), maxUsable)
        
        console.log('Wallet details:', {
          category: wallet.category,
          balance: wallet.balance,
          maxUsable,
          suggestedAmount
        })
        
        if (suggestedAmount > 0 || wallet.category === 'general') {
          // Vérifier si ce crédit a déjà été utilisé dans les paiements provisoires
          const alreadyUsed = this.provisionalPayments
            .filter(p => p.type === 'credit_wallet' && p.data.walletCategory === wallet.category)
            .reduce((sum, p) => sum + p.amount, 0)
          
          const remainingCredit = parseFloat(wallet.balance) - alreadyUsed
          const actualSuggestedAmount = Math.min(remainingCredit, maxUsable)
          
          console.log('Credit calculation:', {
            category: wallet.category,
            balance: wallet.balance,
            alreadyUsed,
            remainingCredit,
            maxUsable,
            actualSuggestedAmount
          })
          
          html += `
            <div class="bg-purple-50 border border-purple-200 rounded-lg p-4" data-wallet-category="${wallet.category}">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="font-medium text-gray-900">Crédit ${this.getCategoryLabel(wallet.category)}</h4>
                  <p class="text-sm text-gray-600">
                    Solde: ${this.formatCurrency(parseFloat(wallet.balance))}
                    ${alreadyUsed > 0 ? `<br><span class="text-purple-600">Utilisé: ${this.formatCurrency(alreadyUsed)}</span>` : ''}
                  </p>
                </div>
                <div class="flex items-center space-x-2">
                  ${actualSuggestedAmount > 0 ? `
                    <button type="button" 
                            class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                            data-action="click->payment-modal#useCreditProactive"
                            data-wallet-category="${wallet.category}"
                            data-suggested-amount="${actualSuggestedAmount.toFixed(2)}">
                      Utiliser ${this.formatCurrency(actualSuggestedAmount)}
                    </button>
                  ` : `
                    <span class="px-3 py-1 bg-gray-200 text-gray-500 rounded text-sm">
                      ${alreadyUsed > 0 ? 'Entièrement utilisé' : 'Non applicable'}
                    </span>
                  `}
                  ${actualSuggestedAmount > 0 ? `
                    <button type="button" 
                            class="p-2 text-gray-500 hover:text-gray-700"
                            data-action="click->payment-modal#toggleManualCredit"
                            data-wallet-category="${wallet.category}">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                  ` : ''}
                </div>
              </div>
              
              <!-- Mode manuel (masqué par défaut) -->
              ${actualSuggestedAmount > 0 ? `
                <div class="manual-credit-form hidden mt-3 pt-3 border-t border-purple-200">
                  <div class="flex items-center space-x-2">
                    <input type="number" 
                           class="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                           step="0.01" 
                           min="0.01" 
                           max="${actualSuggestedAmount.toFixed(2)}"
                           placeholder="Montant"
                           data-wallet-category="${wallet.category}">
                    <button type="button" 
                            class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                            data-action="click->payment-modal#useCreditManual"
                            data-wallet-category="${wallet.category}">
                      + Ajouter
                    </button>
                  </div>
                </div>
              ` : ''}
            </div>
          `
        }
      }
    })
    
    console.log('Final HTML:', html)
    this.clientCreditsTarget.innerHTML = html
  }

  useSubscription(event) {
    const subscriptionId = event.target.dataset.subscriptionId
    
    // Trouver l'abonnement pour obtenir son service_type
    const subscription = this.clientSubscriptionsValue.find(s => 
      s.id === parseInt(subscriptionId) || s.id === subscriptionId
    )
    
    if (!subscription) {
      console.error('Subscription not found:', subscriptionId)
      return
    }
    
    // Vérifier si cet abonnement acheté sur le ticket n'a pas déjà été utilisé
    if (subscription.from_ticket) {
      const ticketItemId = parseInt(subscriptionId.replace('ticket_', ''))
      const alreadyUsed = this.provisionalPayments.some(payment => 
        payment.data.paymentType === 'subscription_from_ticket' && 
        payment.data.ticketItemId === ticketItemId
      )
      
      if (alreadyUsed) {
        alert(`Cet abonnement acheté sur ce ticket a déjà été utilisé pour un paiement`)
        return
      }
    }
    
    // Calculer le montant que peut couvrir cette séance d'abonnement
    // selon son type de service
    console.log('DEBUG useSubscription:', {
      subscription,
      serviceType: subscription.service_type,
      subtotals: this.subtotalsValue,
      totalAmount: this.totalAmountValue,
      provisionalPayments: this.provisionalPayments
    })
    
    const coveredAmount = this.calculateSubscriptionCoverage(subscription.service_type)
    
    if (coveredAmount <= 0) {
      alert(`Aucun service de type "${this.getServiceTypeLabel(subscription.service_type)}" sur ce ticket ne peut être payé avec cet abonnement`)
      return
    }
    
    // Déterminer le type de données à envoyer au backend
    let paymentData
    
    if (subscription.from_ticket) {
      // C'est un abonnement acheté sur ce ticket
      paymentData = {
        paymentType: 'subscription_from_ticket',
        ticketItemId: parseInt(subscriptionId.replace('ticket_', '')),
        amount: coveredAmount,
        serviceType: subscription.service_type
      }
    } else {
      // C'est un abonnement existant du client
      paymentData = {
        paymentType: 'subscription',
        clientSubscriptionId: parseInt(subscriptionId),
        amount: coveredAmount,
        serviceType: subscription.service_type
      }
    }
    
    const payment = {
      id: this.generatePaymentId(),
      type: 'subscription',
      label: `Abonnement ${subscription.plan_name} - ${this.formatCurrency(coveredAmount)}`,
      amount: coveredAmount,
      data: paymentData
    }
    
    this.addProvisionalPayment(payment)
  }

  useCreditProactive(event) {
    const category = event.target.dataset.walletCategory
    const amount = parseFloat(event.target.dataset.suggestedAmount)
    
    console.log('useCreditProactive called:', { category, amount })
    
    // Vérifier que le montant n'excède pas les limites
    const maxUsable = this.calculateMaxUsableAmount(category)
    const wallet = this.clientCreditsValue.find(w => w.category === category)
    
    if (!wallet) {
      console.error('Wallet not found for category:', category)
      return
    }
    
    const maxAllowed = Math.min(parseFloat(wallet.balance), maxUsable)
    
    if (amount > maxAllowed) {
      console.log('Amount exceeds maximum allowed:', { amount, maxAllowed })
      return // Ne rien faire si le montant dépasse la limite
    }
    
    this.addOrUpdateCreditPayment(category, amount)
  }

  toggleManualCredit(event) {
    console.log('toggleManualCredit event target:', event.target)
    console.log('Event target dataset:', event.target.dataset)
    console.log('Event currentTarget dataset:', event.currentTarget.dataset)
    
    // Essayer d'abord avec event.target, puis avec event.currentTarget
    const category = event.target.dataset.walletCategory || event.currentTarget.dataset.walletCategory
    console.log('toggleManualCredit called for category:', category)
    
    if (!category) {
      console.error('No category found in event target or currentTarget')
      return
    }
    
    // Rechercher dans le container des crédits client plutôt que dans l'élément racine
    const container = this.clientCreditsTarget.querySelector(`[data-wallet-category="${category}"]`)
    
    console.log('Container found:', !!container)
    
    if (!container) {
      console.error('Container not found for category:', category)
      console.log('Available containers:', this.clientCreditsTarget.querySelectorAll('[data-wallet-category]'))
      return
    }
    
    const manualForm = container.querySelector('.manual-credit-form')
    console.log('Manual form found:', !!manualForm)
    
    if (!manualForm) {
      console.error('Manual form not found in container')
      return
    }
    
    manualForm.classList.toggle('hidden')
    console.log('Manual form hidden after toggle:', manualForm.classList.contains('hidden'))
    
    if (!manualForm.classList.contains('hidden')) {
      const input = manualForm.querySelector('input')
      console.log('Input found in manual form:', !!input)
      if (input) {
        input.focus()
      }
    }
  }

  useCreditManual(event) {
    const category = event.target.dataset.walletCategory
    const container = this.clientCreditsTarget.querySelector(`[data-wallet-category="${category}"]`)
    
    if (!container) {
      console.error('Container not found for category:', category)
      return
    }
    
    const input = container.querySelector('.manual-credit-form input')
    
    if (!input) {
      console.error('Input not found in manual form')
      return
    }
    
    const amount = parseFloat(input.value)
    
    console.log('useCreditManual called:', { category, amount })
    
    if (!amount || amount <= 0) {
      alert('Veuillez saisir un montant valide')
      return
    }
    
    const maxUsable = this.calculateMaxUsableAmount(category)
    const wallet = this.clientCreditsValue.find(w => w.category === category)
    
    if (!wallet) {
      console.error('Wallet not found for category:', category)
      alert('Erreur: portefeuille de crédit non trouvé')
      return
    }
    
    const maxAllowed = Math.min(parseFloat(wallet.balance), maxUsable)
    
    if (amount > maxAllowed) {
      alert(`Montant maximum autorisé: ${this.formatCurrency(maxAllowed)}`)
      return
    }
    
    this.addOrUpdateCreditPayment(category, amount)
    
    // Masquer le formulaire et vider le champ
    container.querySelector('.manual-credit-form').classList.add('hidden')
    input.value = ''
  }

  addCreditPayment(category, amount) {
    const payment = {
      id: this.generatePaymentId(),
      type: 'credit_wallet',
      label: `Crédit ${this.getCategoryLabel(category)} - ${this.formatCurrency(amount)}`,
      amount: amount,
      data: {
        paymentType: 'credit_wallet',
        walletCategory: category,
        amount: amount
      }
    }
    
    this.addProvisionalPayment(payment)
  }

  addOrUpdateCreditPayment(category, amount) {
    // Chercher un paiement existant pour cette catégorie de crédit
    const existingPaymentIndex = this.provisionalPayments.findIndex(
      p => p.type === 'credit_wallet' && p.data.walletCategory === category
    )
    
    if (existingPaymentIndex !== -1) {
      // Mettre à jour le paiement existant
      const existingPayment = this.provisionalPayments[existingPaymentIndex]
      const newAmount = existingPayment.amount + amount
      
      // Vérifier que le nouveau montant ne dépasse pas les limites
      const maxUsable = this.calculateMaxUsableAmount(category)
      const wallet = this.clientCreditsValue.find(w => w.category === category)
      const maxAllowed = Math.min(parseFloat(wallet.balance), maxUsable + existingPayment.amount) // +existingPayment.amount car il sera soustrait dans calculateMaxUsableAmount
      
      if (newAmount > maxAllowed) {
        console.log('Cannot add more credit, would exceed limits:', { newAmount, maxAllowed })
        return
      }
      
      existingPayment.amount = newAmount
      existingPayment.label = `Crédit ${this.getCategoryLabel(category)} - ${this.formatCurrency(newAmount)}`
      existingPayment.data.amount = newAmount
      
      console.log('Updated existing credit payment:', existingPayment)
      this.updateDisplay()
      this.updateAvailableOptions()
    } else {
      // Créer un nouveau paiement
      this.addCreditPayment(category, amount)
    }
  }

  fillRemainingAmount(event) {
    event.preventDefault()
    
    const remaining = this.getRemainingAmount()
    if (remaining > 0 && this.hasAmountInputTarget) {
      this.amountInputTarget.value = remaining.toFixed(2)
      this.amountInputTarget.focus()
    }
  }

  addStandardPayment(event) {
    event.preventDefault()
    
    const form = event.target.closest('form')
    const formData = new FormData(form)
    const paymentMethodId = formData.get('payment_method_id')
    const amount = parseFloat(formData.get('amount'))
    
    if (!paymentMethodId || !amount || amount <= 0) {
      alert('Veuillez sélectionner une méthode de paiement et saisir un montant')
      return
    }
    
    // Trouver le nom de la méthode de paiement
    const methodRadio = form.querySelector(`input[value="${paymentMethodId}"]`)
    const methodLabel = form.querySelector(`label[for="${methodRadio.id}"] p`).textContent.trim()
    
    const payment = {
      id: this.generatePaymentId(),
      type: 'standard',
      label: `${methodLabel} - ${this.formatCurrency(amount)}`,
      amount: amount,
      data: {
        paymentType: 'standard',
        paymentMethodId: parseInt(paymentMethodId),
        amount: amount
      }
    }
    
    this.addProvisionalPayment(payment)
    form.reset()
  }

  addProvisionalPayment(payment) {
    console.log('Adding provisional payment:', payment)
    this.provisionalPayments.push(payment)
    console.log('All provisional payments:', this.provisionalPayments)
    this.updateDisplay()
    this.updateAvailableOptions()
  }

  removeProvisionalPayment(event) {
    const button = event.target.closest('button')
    const paymentId = button.dataset.paymentId
    console.log('Removing provisional payment with ID:', paymentId)
    this.provisionalPayments = this.provisionalPayments.filter(p => p.id !== paymentId)
    console.log('Remaining provisional payments:', this.provisionalPayments)
    this.updateDisplay()
    this.updateAvailableOptions()
  }

  updateDisplay() {
    this.updateAmounts()
    this.updatePaymentsList()
    this.updateValidateButton()
    this.updateAmountInputMax()
  }

  updateAmounts() {
    const totalPaid = this.provisionalPayments.reduce((sum, p) => sum + p.amount, 0)
    const remaining = this.ticketTotalValue - totalPaid
    
    this.totalAmountTarget.textContent = this.formatCurrency(this.ticketTotalValue)
    this.remainingAmountTarget.textContent = this.formatCurrency(remaining)
    
    // Mise à jour des couleurs
    if (remaining === 0) {
      this.remainingAmountTarget.className = "text-2xl font-bold text-green-600"
    } else if (remaining > 0) {
      this.remainingAmountTarget.className = "text-2xl font-bold text-red-600"
    } else {
      this.remainingAmountTarget.className = "text-2xl font-bold text-orange-600"
    }
  }

  updatePaymentsList() {
    if (!this.hasPaymentsListTarget) return
    
    let html = ""
    
    this.provisionalPayments.forEach(payment => {
      html += `
        <div class="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md">
          <span class="text-sm">${payment.label}</span>
          <button type="button" 
                  class="text-red-600 hover:text-red-800"
                  data-action="click->payment-modal#removeProvisionalPayment"
                  data-payment-id="${payment.id}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      `
    })
    
    if (html === "") {
      html = '<p class="text-gray-500 text-sm text-center py-4">Aucun paiement ajouté</p>'
    }
    
    this.paymentsListTarget.innerHTML = html
  }

  updateValidateButton() {
    const totalPaid = this.provisionalPayments.reduce((sum, p) => sum + p.amount, 0)
    const isComplete = Math.abs(totalPaid - this.ticketTotalValue) < 0.01
    
    this.validateButtonTarget.disabled = !isComplete
    
    if (isComplete) {
      this.validateButtonTarget.className = "flex-1 inline-flex justify-center rounded-md border border-transparent bg-green-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-sz-primary-500 focus:ring-offset-2"
      this.validateButtonTarget.textContent = "Valider le paiement"
    } else {
      this.validateButtonTarget.className = "flex-1 inline-flex justify-center rounded-md border border-transparent bg-gray-400 py-2 px-4 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sz-primary-500 focus:ring-offset-2 cursor-not-allowed"
      this.validateButtonTarget.textContent = "Paiement incomplet"
    }
  }

  updateAmountInputMax() {
    if (this.hasAmountInputTarget) {
      const remaining = this.getRemainingAmount()
      this.amountInputTarget.max = remaining.toFixed(2)
    }
    
    if (this.hasRemainingAmountButtonTarget) {
      const remaining = this.getRemainingAmount()
      this.remainingAmountButtonTarget.textContent = this.formatCurrency(remaining)
    }
  }

  updateAvailableOptions() {
    // Recalculer et re-rendre les options disponibles
    this.renderClientCredits()
    this.renderClientSubscriptions()
  }

  validatePayment(event) {
    event.preventDefault()
    
    if (this.provisionalPayments.length === 0) {
      alert('Aucun paiement à traiter')
      return
    }
    
    const totalPaid = this.provisionalPayments.reduce((sum, p) => sum + p.amount, 0)
    if (Math.abs(totalPaid - this.ticketTotalValue) >= 0.01) {
      alert('Le montant total des paiements ne correspond pas au total du ticket')
      return
    }
    
    // Préparer le payload pour l'envoi au backend
    const payload = this.provisionalPayments.map(p => p.data)
    
    // Envoyer la requête
    this.submitSplitPayment(payload)
  }

  async submitSplitPayment(payload) {
    try {
      const response = await fetch('/pos/split_payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify({
          ticket_id: this.ticketIdValue,
          payment_payload: payload
        })
      })
      
      if (response.ok) {
        const turboStream = await response.text()
        Turbo.renderStreamMessage(turboStream)
      } else {
        const error = await response.text()
        alert('Erreur lors du traitement du paiement: ' + error)
      }
    } catch (error) {
      console.error('Erreur de réseau:', error)
      alert('Erreur de connexion')
    }
  }

  // Méthodes utilitaires
  
  calculateMaxUsableAmount(category) {
    console.log('calculateMaxUsableAmount called with category:', category)
    
    const usedInPayments = this.provisionalPayments
      .filter(p => p.type === 'credit_wallet' && p.data.walletCategory === category)
      .reduce((sum, p) => sum + p.amount, 0)
    
    const remaining = this.getRemainingAmount()
    
    console.log('Calculation details:', {
      category,
      usedInPayments,
      remaining,
      subtotals: this.subtotalsValue
    })
    
    switch (category) {
      case 'treatments':
        const treatmentsMax = Math.max(0, Math.min(parseFloat(this.subtotalsValue.treatments || 0) - usedInPayments, remaining))
        console.log('Treatments max:', treatmentsMax)
        return treatmentsMax
      case 'sunbeds':
        const sunbedsMax = Math.max(0, Math.min(parseFloat(this.subtotalsValue.sunbeds || 0) - usedInPayments, remaining))
        console.log('Sunbeds max:', sunbedsMax)
        return sunbedsMax
      default:
        console.log('Default max (remaining):', remaining)
        return remaining
    }
  }

  getRemainingAmount() {
    const totalPaid = this.provisionalPayments.reduce((sum, p) => sum + p.amount, 0)
    return this.ticketTotalValue - totalPaid
  }

  getCategoryLabel(category) {
    const labels = {
      'products': 'Produits',
      'treatments': 'Soins',
      'sunbeds': 'Bancs solaires',
      'general': 'Général'
    }
    return labels[category] || category
  }

  getServiceTypeLabel(serviceType) {
    const labels = {
      'products': 'produits',
      'treatments': 'soins',
      'sunbeds': 'bancs solaires',
      'general': 'services généraux'
    }
    return labels[serviceType] || serviceType
  }

  generatePaymentId() {
    return Date.now() + Math.random().toString(36).substr(2, 9)
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  calculateSubscriptionCoverage(serviceType) {
    // Calculer le montant qu'UNE utilisation d'abonnement peut couvrir
    let serviceSubtotal = 0
    
    // Récupérer le sous-total du type de service
    switch (serviceType) {
      case 'sunbeds':
        serviceSubtotal = parseFloat(this.subtotalsValue.sunbeds || 0)
        break
      case 'treatments':
        serviceSubtotal = parseFloat(this.subtotalsValue.treatments || 0)
        break
      default:
        console.warn('Unknown service type:', serviceType)
        return 0
    }

    // Calculer combien a déjà été payé par des abonnements du même type
    const alreadyPaidBySubscriptions = this.provisionalPayments
      .filter(payment => {
        if (payment.type !== 'subscription') return false
        // Vérifier que c'est le même type de service
        return payment.data.serviceType === serviceType
      })
      .reduce((sum, payment) => sum + payment.amount, 0)

    // Calculer le montant restant pour ce type de service
    const remainingForServiceType = serviceSubtotal - alreadyPaidBySubscriptions

    // Calculer le montant total restant à payer 
    const totalAlreadyPaid = this.provisionalPayments.reduce((sum, payment) => sum + payment.amount, 0)
    const totalRemainingToPay = this.ticketTotalValue - totalAlreadyPaid
    
    // L'abonnement peut couvrir au maximum le minimum entre :
    // 1. Ce qui reste à payer pour ce type de service
    // 2. Ce qui reste à payer au total
    const coverableAmount = Math.min(remainingForServiceType, totalRemainingToPay)
    
    return Math.max(0, coverableAmount)
  }
}
